// =============================================================================
// notificationsSync.js — the bit that makes reorder alerts real.
//
// WHAT WAS WRONG. The alerting plumbing has existed for a while: an outbox, a
// once-a-day throttle, a flagged → ordered → delivered → resolved loop
// (migrations 0044 + 0046). It has never carried a single message, because the
// only functions that write to it are granted to `service_role` and a kitchen
// tablet does not have — and must never have — a service_role key. So "running
// low" still means a mailto: link somebody has to notice and tap, and mostly
// nobody does. Migration 0090 opens one narrow door for the tablet; this walks
// through it.
//
// WHAT THIS DOES. When the kitchen flags an item running low or out of stock, it
// ALSO raises a proper, de-duplicated alert in Supabase. When the item is
// delivered or goes back in stock, it closes that alert.
//
// WHAT THIS DOES NOT DO — and cannot be made to do:
//   * It does not send an email, and there is no sender anywhere. Rows queue up
//     and sit there. Turning on a dispatcher is a separate, deliberate decision,
//     and a better one to make after watching what the queue fills up with.
//   * It does not touch the existing mailto: prompt on the screen, or the Apps
//     Script / Sheet write. Both stay exactly as they are. This is a dual-write,
//     never a replacement.
//   * It cannot slow or break the kitchen. Every call swallows its own errors,
//     returns rather than throws, and is fired beside the live path, never in
//     front of it.
//
// THE ONE-LINE KILL: set NOTIFY_SYNC_ENABLED to false, below. Nothing else needs
// to change and nothing else is affected — the kitchen behaves exactly as it did
// before this file existed.
//
// Inert unless dualRunEnabled() — the same single dual-run switch every other
// module uses (the four live kiosk hosts, or ?dualrun=on). On staging, a preview
// build or localhost this file does nothing at all.
//
// The rules live next door in notificationsPlan.js, which has no Supabase import
// so it can be tested hermetically. This file is only the hand that makes the call.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled } from "./stockSync.js";
import { planReorderSignal, planIncidentSignal, tokenForTap, GOOD_FLAG_RESULTS } from "./notificationsPlan.js";

// ---- the one-line kill ------------------------------------------------------
// false = stop raising and closing alerts. Everything else in the kitchen is
// untouched: the Sheet write, the on-screen prompt, the stock_activity shadow.
export const NOTIFY_SYNC_ENABLED = true;

export { FLAG_FOR, STEP_FOR } from "./notificationsPlan.js";

// ---- one retry, while the pass is still warm --------------------------------
// Kiosk tablets sit on shopping-centre wifi and drop out for a second or two at a
// time. Losing an alert to a blip that has already passed is a waste, so a call
// that fails to REACH Supabase gets exactly one more go, ~1.2s later.
//
// This is safe only because of the token: a retry that turns out to be a
// duplicate of a first attempt that actually landed comes back 'duplicate' and
// writes nothing. Without the idempotency key this would be the thing that
// created double rows, not the thing that prevented losing one.
//
// One retry, not a queue. A persistent offline buffer is a bigger and more
// awkward idea than it looks — see the note in the review pack: replaying an
// alert minutes or hours later would stamp it with whoever is logged in at flush
// time and with the London day it was flushed on, not the day it happened. Both
// are quietly wrong in a record whose whole value is who noticed and when.
const RETRY_AFTER_MS = 1200;
const looksLikeNetwork = (e) =>
  /failed to fetch|networkerror|network request failed|load failed|timeout|econn|fetch failed/i
    .test(String(e?.message || e || ""));

async function callOnce(rpc, args) {
  try {
    const res = await supabase.rpc(rpc, args);
    return res || {};
  } catch (e) {
    return { error: e, threw: true };
  }
}

async function callWithOneRetry(rpc, args) {
  const first = await callOnce(rpc, args);
  if (!first.error || !looksLikeNetwork(first.error)) return first;
  console.warn(`[notify-sync] ${rpc} couldn't reach Supabase — one more go in ${RETRY_AFTER_MS}ms`);
  await new Promise((r) => setTimeout(r, RETRY_AFTER_MS));
  return callOnce(rpc, args);
}

// One call per kitchen action. `action` is the app's own name for it
// (stockRunningLow / stockOutOfStock / stockDelivered / backInStock); anything
// else is ignored, silently and cheaply, because most kitchen actions have
// nothing to do with alerting.
//
// `pass` is the result of stockSync.ensurePass() — the ~15-minute staff pass the
// PIN just minted. We never send a staff id: the database takes it from the pass
// itself. The raw PIN never reaches this file.
//
// Returns a small object for logging and tests. NEVER throws, never rejects.
export async function recordReorderSignal({ siteKey, item, action, pass = null, now = Date.now() } = {}) {
  try {
    if (!NOTIFY_SYNC_ENABLED) return { ok: true, skipped: "switched off" };
    if (!dualRunEnabled()) return { ok: true, skipped: "dual-run off" };

    const plan = planReorderSignal({ siteKey, item, action, pass, now });
    if (plan.skip) return { ok: true, skipped: plan.skip };

    const { data, error } = await callWithOneRetry(plan.rpc, plan.args);
    if (error) {
      console.warn(`[notify-sync] ${plan.rpc} rejected:`, error.message || error);
      return { ok: false, error };
    }

    if (plan.rpc === "notification_flag_kiosk") {
      // The function answers in plain words: queued | suppressed | duplicate |
      // no_pass | bad_flag | no_item | no_token | error.
      if (GOOD_FLAG_RESULTS.has(data)) return { ok: true, result: data };
      console.warn("[notify-sync] alert not recorded:", data);
      return { ok: false, result: data };
    }
    // progress: moved:N | nothing_open | no_pass | bad_step | no_item | error.
    // 'nothing_open' is a perfectly normal answer — plenty of stock moves
    // without anybody having flagged it first — so it is not a failure.
    return { ok: true, result: data };
  } catch (e) {
    // Best-effort, always. An alert failing must never be the reason a chef
    // can't mark something out of stock.
    console.warn("[notify-sync] recordReorderSignal threw:", e?.message || e);
    return { ok: false, error: e };
  }
}

// ---- the urgent-incident page (a deliberate half-sibling of the reorder one) -
// A running-low tap can afford to be quiet: it hides behind dualRunEnabled()
// (inert on staging / preview / localhost) and only console.warns a refusal. An
// URGENT incident can afford neither — it is a safety page ("a customer
// reacted", "the freezer's down") and dropping it silently on a non-kiosk host,
// or swallowing a refusal into the console, is exactly the failure we may not
// have. So this diverges on purpose:
//   * NO dualRunEnabled() gate. The row write (incidentsSync.recordIncident) is
//     already dual-run gated; by the time we're called with a real pass it
//     landed, and re-gating here could only drop a page a real incident sits on.
//   * A non-GOOD result is RETURNED, not just warned, so the tile can react. We
//     still swallow THROWS — a failed page must never stop a staffer filing the
//     incident — but a REFUSAL is surfaced.
// It still needs a valid app_session pass (the one recordIncident minted), so the
// whole tap costs ONE login; without one the DB answers 'no_pass' and there is no
// truthful "who filed it", so we short-circuit to a surfaced no_pass.
export async function recordIncidentSignal({ siteKey, type, severity, summary, token = null, pass = null, now = Date.now() } = {}) {
  try {
    if (!NOTIFY_SYNC_ENABLED) return { ok: true, skipped: "switched off" };
    // Deliberately NO dualRunEnabled() check here — see the note above.
    if (!pass || pass.ok === false) {
      console.warn("[notify-sync] urgent incident page has no staff pass — not recorded");
      return { ok: false, result: "no_pass" };
    }
    // Idempotency key = the incident's OWN per-submit token (the same uuid the row
    // write uses as idem_key). A retried/double-tapped SUBMIT reuses it and
    // collapses to one page; two genuinely distinct incidents get distinct tokens
    // and BOTH page — matching migration 0660's per-submit dedupe, and keeping the
    // row and the page on one key. Falls back to a same-tap CONTENT token only if
    // the tile didn't thread one through (defensive; it always does).
    const tok = token || tokenForTap(siteKey, "incident:" + String(type ?? "").trim(), summary, now);
    const plan = planIncidentSignal({ siteKey, type, severity, summary, token: tok });
    if (plan.skip) return { ok: true, skipped: plan.skip };

    const { data, error } = await callWithOneRetry(plan.rpc, plan.args);
    if (error) {
      console.warn(`[notify-sync] ${plan.rpc} rejected:`, error.message || error);
      return { ok: false, error };
    }
    if (GOOD_FLAG_RESULTS.has(data)) return { ok: true, result: data };
    console.warn("[notify-sync] urgent incident page not recorded:", data);
    return { ok: false, result: data };
  } catch (e) {
    console.warn("[notify-sync] recordIncidentSignal threw:", e?.message || e);
    return { ok: false, error: e };
  }
}
